import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Modal, Form, Spinner, Badge, Card } from 'react-bootstrap';
import { FaTrash, FaTelegram, FaPlus, FaCheckCircle, FaClock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import api from '../services/api';
import AuthContext from '../context/AuthContext';

const TelegramBotPage = () => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();

    const [operators, setOperators] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '',
        phone_country_code: '+57',
        phone: '',
        user_id: '',
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [opsRes, usersRes] = await Promise.all([
                api.get('/bot/operators'),
                api.get('/users'),
            ]);
            setOperators(opsRes.data);
            setUsers(usersRes.data);
        } catch {
            toast.error('Error al cargar los operadores del bot');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFormChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleOpenModal = () => {
        setForm({ name: '', phone_country_code: '+57', phone: '', user_id: '' });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error('El nombre y el teléfono son obligatorios');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim(),
                phone_country_code: form.phone_country_code.trim(),
                ...(form.user_id ? { user_id: Number(form.user_id) } : {}),
            };
            const res = await api.post('/bot/operators', payload);
            setOperators([res.data, ...operators]);
            setShowModal(false);
            toast.success('Operador agregado correctamente');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al agregar el operador');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (op) => {
        try {
            const res = await api.put(`/bot/operators/${op.id}`, { is_active: !op.is_active });
            setOperators(operators.map(o => o.id === op.id ? res.data : o));
        } catch {
            toast.error('Error al actualizar el operador');
        }
    };

    const handleDelete = async (op) => {
        const result = await Swal.fire({
            title: '¿Eliminar operador?',
            text: `Se eliminará a ${op.name} del bot. Deberá ser agregado nuevamente para volver a usarlo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) return;

        try {
            await api.delete(`/bot/operators/${op.id}`);
            setOperators(operators.filter(o => o.id !== op.id));
            toast.success('Operador eliminado');
        } catch {
            toast.error('Error al eliminar el operador');
        }
    };

    const linkedCount = operators.filter(o => o.telegram_chat_id).length;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-0">
                        <FaTelegram className="me-2 text-primary" />
                        Bot de Telegram
                    </h4>
                    <small className="text-muted">
                        Gestiona quién puede usar el bot transaccional de tu negocio
                    </small>
                </div>
                <Button variant="primary" size="sm" onClick={handleOpenModal}>
                    <FaPlus className="me-1" /> Agregar operador
                </Button>
            </div>

            {/* Info card */}
            <Card className="mb-4 border-0 bg-light">
                <Card.Body className="py-3">
                    <p className="mb-1 small">
                        <strong>¿Cómo funciona?</strong>
                    </p>
                    <ol className="mb-0 small ps-3">
                        <li>Agrega el nombre y teléfono del operador aquí.</li>
                        <li>El operador debe iniciar el bot <strong>@evazard_admin_ai_bot</strong> en Telegram.</li>
                        <li>El bot le pedirá compartir su número de teléfono para verificar su identidad.</li>
                        <li>Una vez vinculado, podrá registrar ventas, productos, clientes y más desde Telegram.</li>
                    </ol>
                </Card.Body>
            </Card>

            {isLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" size="sm" />
                </div>
            ) : operators.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <FaTelegram size={40} className="mb-3 opacity-25" />
                    <p>Aún no tienes operadores registrados.</p>
                    <Button variant="primary" size="sm" onClick={handleOpenModal}>
                        <FaPlus className="me-1" /> Agregar primer operador
                    </Button>
                </div>
            ) : (
                <>
                    <p className="text-muted small mb-3">
                        {linkedCount} de {operators.length} operador(es) vinculado(s) a Telegram
                    </p>
                    <Table responsive hover>
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Teléfono</th>
                                <th>Estado Telegram</th>
                                <th>Usuario del sistema</th>
                                <th>Activo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {operators.map(op => (
                                <tr key={op.id} className={!op.is_active ? 'opacity-50' : ''}>
                                    <td>{op.name}</td>
                                    <td>{op.phone_country_code} {op.phone}</td>
                                    <td>
                                        {op.telegram_chat_id ? (
                                            <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                                <FaCheckCircle /> Vinculado
                                            </Badge>
                                        ) : (
                                            <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                                <FaClock /> Pendiente
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <span className="text-muted small">
                                            {op.user_name || <em>Sin vincular</em>}
                                        </span>
                                    </td>
                                    <td>
                                        <Form.Check
                                            type="switch"
                                            checked={op.is_active}
                                            onChange={() => handleToggleActive(op)}
                                            aria-label={`Activar/desactivar ${op.name}`}
                                        />
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDelete(op)}
                                        >
                                            <FaTrash />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {/* Add Operator Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Agregar operador del bot</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                name="name"
                                value={form.name}
                                onChange={handleFormChange}
                                placeholder="ej: Juan Pérez"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Teléfono *</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    name="phone_country_code"
                                    value={form.phone_country_code}
                                    onChange={handleFormChange}
                                    placeholder="+57"
                                    style={{ width: '90px', flexShrink: 0 }}
                                />
                                <Form.Control
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleFormChange}
                                    placeholder="3001234567"
                                    required
                                />
                            </div>
                            <Form.Text className="text-muted">
                                Este número debe coincidir con el de Telegram del operador.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Vincular a usuario del sistema <span className="text-muted">(opcional)</span></Form.Label>
                            <Form.Select
                                name="user_id"
                                value={form.user_id}
                                onChange={handleFormChange}
                            >
                                <option value="">— Sin vincular —</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Al vincular, las acciones del bot quedarán registradas bajo ese usuario.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" /> : 'Agregar operador'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default TelegramBotPage;
